/**
 * CollectionIntel — Real-Time Intelligence Collection Progress
 *
 * Shows each intelligence source being queried during a scan,
 * with ICD-203 reliability badges, progress status, and live findings count.
 *
 * Designed for HNW/UHNW executive and family security intelligence cycle.
 * PIR-001 through PIR-008 coverage displayed per source.
 *
 * Usage:
 *   <CollectionIntel scanId={scanId} subject="Jane Smith" onComplete={fn} />
 *
 * Polls GET /api/collection/progress/{scanId} every 2 seconds.
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import api from '../utils/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface SourceStatus {
  source_key: string
  display_name: string
  source_type: 'OSINT' | 'TECHINT' | 'HUMINT'
  reliability: 'A' | 'B' | 'C' | 'D' | 'E' | 'F'
  reliability_label: string
  confidence: '1' | '2' | '3' | '4' | '5' | '6'
  pirs: string[]
  description: string
  api_key_required: boolean
  requires_tor: boolean
  cost: 'FREE' | 'PAID' | 'FREEMIUM'
  status: 'pending' | 'active' | 'complete' | 'skipped' | 'error'
  findings_count: number
  error: string | null
  started_at: string | null
  completed_at: string | null
}

interface PIRSummary {
  name: string
  question: string
  priority: string
}

interface CollectionProgress {
  scan_id: string
  subject: string
  status: string
  started_at: string
  completed_at: string | null
  sources: SourceStatus[]
  total_sources: number
  complete_count: number
  total_findings: number
  progress_pct: number
  pir_framework: Record<string, PIRSummary>
}

interface Props {
  scanId: string
  subject?: string
  onComplete?: (progress: CollectionProgress) => void
  compact?: boolean   // compact mode for embedding in scan page
}

// ── ICD-203 Reliability Badge ─────────────────────────────────────────────────

const RELIABILITY_COLORS: Record<string, string> = {
  A: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  B: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  C: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  D: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  E: 'bg-red-500/20 text-red-300 border-red-500/30',
  F: 'bg-dark-600/50 text-dark-400 border-dark-600',
}

function ReliabilityBadge({ rating, label }: { rating: string; label: string }) {
  const cls = RELIABILITY_COLORS[rating] || RELIABILITY_COLORS.F
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono font-bold border ${cls}`}
      title={label}
    >
      {rating}
    </span>
  )
}

// ── Source Type Chip ──────────────────────────────────────────────────────────

function SourceTypeBadge({ type }: { type: string }) {
  const cls =
    type === 'TECHINT'
      ? 'bg-purple-500/15 text-purple-300'
      : type === 'HUMINT'
      ? 'bg-rose-500/15 text-rose-300'
      : 'bg-sky-500/15 text-sky-300'
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {type}
    </span>
  )
}

// ── PIR Tag ───────────────────────────────────────────────────────────────────

function PIRTag({ pir }: { pir: string }) {
  const priority = pir.replace('PIR-', 'P')
  return (
    <span className="inline-block px-1 py-0.5 rounded text-xs font-mono text-ginger-400/80 bg-ginger-500/10">
      {priority}
    </span>
  )
}

// ── Status Icon ───────────────────────────────────────────────────────────────

function StatusIcon({ status, findingsCount }: { status: string; findingsCount: number }) {
  if (status === 'active') {
    return (
      <div className="w-5 h-5 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
    )
  }
  if (status === 'complete') {
    if (findingsCount > 0) {
      return (
        <div className="w-5 h-5 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center flex-shrink-0">
          <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01" />
          </svg>
        </div>
      )
    }
    return (
      <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div className="w-5 h-5 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    )
  }
  if (status === 'skipped') {
    return (
      <div className="w-5 h-5 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center flex-shrink-0">
        <span className="text-dark-500 text-xs">—</span>
      </div>
    )
  }
  // pending
  return (
    <div className="w-5 h-5 rounded-full bg-dark-800 border border-dark-700 flex-shrink-0" />
  )
}

// ── Source Row ────────────────────────────────────────────────────────────────

function SourceRow({ source }: { source: SourceStatus; pirFramework: Record<string, PIRSummary> }) {
  const isActive = source.status === 'active'
  const isComplete = source.status === 'complete'
  const hasFindings = source.findings_count > 0

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`relative flex items-start gap-3 px-4 py-3 rounded-lg border transition-all duration-300 ${
        isActive
          ? 'bg-dark-800 border-ginger-500/30 shadow-lg shadow-ginger-500/5'
          : isComplete && hasFindings
          ? 'bg-dark-900/80 border-red-500/20'
          : isComplete
          ? 'bg-dark-900/50 border-dark-700/50'
          : source.status === 'error'
          ? 'bg-dark-900/50 border-orange-500/20'
          : source.status === 'skipped'
          ? 'bg-dark-900/30 border-dark-800/50 opacity-50'
          : 'bg-dark-900/30 border-dark-800/30 opacity-40'
      }`}
    >
      {/* Active glow line */}
      {isActive && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-ginger-500 rounded-l-lg" />
      )}

      {/* Status icon */}
      <div className="mt-0.5">
        <StatusIcon status={source.status} findingsCount={source.findings_count} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${
              isActive ? 'text-ginger-300' :
              isComplete ? 'text-dark-200' :
              source.status === 'error' ? 'text-orange-300' :
              'text-dark-400'
            }`}>
              {source.display_name}
            </span>

            {/* ICD-203 Badge */}
            <ReliabilityBadge
              rating={source.reliability}
              label={`ICD-203 ${source.reliability}: ${source.reliability_label}`}
            />
            <SourceTypeBadge type={source.source_type} />

            {source.requires_tor && (
              <span className="inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-500/15 text-violet-300">
                TOR
              </span>
            )}
          </div>

          {/* Findings count */}
          {isComplete && (
            <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${
              hasFindings
                ? 'bg-red-500/20 text-red-300'
                : 'bg-green-500/15 text-green-400'
            }`}>
              {hasFindings ? `${source.findings_count} FOUND` : 'CLEAR'}
            </span>
          )}
          {source.status === 'error' && (
            <span className="text-xs text-orange-400 flex-shrink-0">ERROR</span>
          )}
          {source.status === 'skipped' && (
            <span className="text-xs text-dark-500 flex-shrink-0">SKIPPED</span>
          )}
        </div>

        {/* Description */}
        <p className="text-xs text-dark-500 mt-0.5 truncate">{source.description}</p>

        {/* Error message */}
        {source.error && source.status === 'error' && (
          <p className="text-xs text-orange-400/70 mt-1">{source.error}</p>
        )}

        {/* PIR tags */}
        {source.pirs.length > 0 && (isActive || isComplete) && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            <span className="text-xs text-dark-600 mr-1">PIR:</span>
            {source.pirs.map((pir) => (
              <PIRTag key={pir} pir={pir} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  )
}

// ── Summary Stats ─────────────────────────────────────────────────────────────

function SummaryStats({ progress }: { progress: CollectionProgress }) {
  const { sources } = progress
  const complete = sources.filter((s) => s.status === 'complete').length
  const active = sources.filter((s) => s.status === 'active').length
  const pending = sources.filter((s) => s.status === 'pending').length
  const skipped = sources.filter((s) => s.status === 'skipped').length
  const withFindings = sources.filter((s) => s.findings_count > 0).length

  return (
    <div className="grid grid-cols-3 gap-3 mb-4">
      <div className="bg-dark-800/60 rounded-lg px-3 py-2 text-center border border-dark-700/50">
        <div className="text-xl font-bold text-ginger-400">{progress.total_sources}</div>
        <div className="text-xs text-dark-500 mt-0.5">Sources</div>
      </div>
      <div className="bg-dark-800/60 rounded-lg px-3 py-2 text-center border border-dark-700/50">
        <div className={`text-xl font-bold ${progress.total_findings > 0 ? 'text-red-400' : 'text-green-400'}`}>
          {progress.total_findings}
        </div>
        <div className="text-xs text-dark-500 mt-0.5">Findings</div>
      </div>
      <div className="bg-dark-800/60 rounded-lg px-3 py-2 text-center border border-dark-700/50">
        <div className="text-xl font-bold text-dark-300">{progress.progress_pct}%</div>
        <div className="text-xs text-dark-500 mt-0.5">Complete</div>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function CollectionIntel({ scanId, subject, onComplete, compact = false }: Props) {
  const [progress, setProgress] = useState<CollectionProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout>>()
  const doneRef = useRef(false)

  const poll = useCallback(async () => {
    if (doneRef.current) return
    try {
      const res = await api.get(`/collection/progress/${scanId}`)
      const data: CollectionProgress = res.data
      setProgress(data)

      if (data.status === 'complete') {
        doneRef.current = true
        onComplete?.(data)
      } else {
        pollRef.current = setTimeout(poll, 2000)
      }
    } catch (err: any) {
      // If 404, scan hasn't started yet — keep polling
      if (err?.response?.status === 404) {
        pollRef.current = setTimeout(poll, 2000)
      } else {
        setError('Collection tracker unavailable')
      }
    }
  }, [scanId, onComplete])

  useEffect(() => {
    poll()
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [poll])

  if (error) {
    return (
      <div className="p-4 bg-dark-800/50 rounded-xl border border-dark-700 text-center text-dark-400 text-sm">
        {error}
      </div>
    )
  }

  if (!progress) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-dark-800/40 rounded-lg animate-pulse border border-dark-700/30" />
        ))}
      </div>
    )
  }

  // Sort: active first, then pending, then complete/skipped
  const sorted = [...progress.sources].sort((a, b) => {
    const order = { active: 0, pending: 1, complete: 2, error: 3, skipped: 4 }
    return (order[a.status] ?? 5) - (order[b.status] ?? 5)
  })

  return (
    <div className={compact ? '' : 'max-w-2xl mx-auto'}>
      {/* Header */}
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">Intelligence Collection</h2>
            {subject && (
              <p className="text-dark-400 text-sm mt-0.5">
                Scanning {progress.total_sources} sources for{' '}
                <span className="text-dark-200 font-medium">{subject}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-500 font-mono">ICD-203</span>
            {progress.status === 'active' && (
              <div className="w-2 h-2 rounded-full bg-ginger-500 animate-pulse" />
            )}
            {progress.status === 'complete' && (
              <div className="w-2 h-2 rounded-full bg-green-500" />
            )}
          </div>
        </div>
      )}

      {/* Overall progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-dark-500 mb-1.5">
          <span>
            {progress.complete_count} of {progress.total_sources} sources queried
          </span>
          <span>{progress.progress_pct}%</span>
        </div>
        <div className="w-full h-1.5 bg-dark-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-ginger-500 to-orange-400 rounded-full"
            animate={{ width: `${progress.progress_pct}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Stats */}
      {!compact && <SummaryStats progress={progress} />}

      {/* Source list */}
      <div className="space-y-2">
        <AnimatePresence>
          {sorted.map((source) => (
            <SourceRow
              key={source.source_key}
              source={source}
              pirFramework={progress.pir_framework}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* PIR Legend (only when not compact and there are findings) */}
      {!compact && progress.total_findings > 0 && progress.status === 'complete' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-dark-800/50 rounded-xl border border-dark-700"
        >
          <h3 className="text-sm font-semibold mb-3 text-dark-300">Priority Intelligence Requirements (PIR)</h3>
          <div className="space-y-2">
            {Object.entries(progress.pir_framework).map(([key, pir]) => {
              // Check if any source with findings covers this PIR
              const covered = progress.sources.some(
                (s) => s.findings_count > 0 && s.pirs.includes(key)
              )
              if (!covered) return null
              return (
                <div key={key} className="flex items-start gap-2">
                  <span className="text-xs font-mono text-ginger-400 flex-shrink-0 mt-0.5">{key}</span>
                  <div>
                    <div className="text-xs font-semibold text-dark-200">{pir.name}</div>
                    <div className="text-xs text-dark-500">{pir.question}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* ICD-203 Legend */}
      {!compact && (
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-xs text-dark-600">ICD-203 Reliability:</span>
          {(['A', 'B', 'C', 'D', 'E', 'F'] as const).map((r) => (
            <ReliabilityBadge key={r} rating={r} label="" />
          ))}
          <span className="text-xs text-dark-600 ml-1">A=Best · F=Unknown</span>
        </div>
      )}
    </div>
  )
}
